import React from 'react';
import { useParams } from 'react-router-dom';
import { useSectionSearch } from './SectionTabs';
import SPAButton from '../shared/SPAButton';
import SPADialog from '../shared/SPADialog';
import RoutedSPADialog from '../shared/RoutedSPADialog';
import SPAPill from '../shared/SPAPill';
import useAssignmentSyncState from './useAssignmentSyncState';
import type { CourseRow } from './ClassMap';
import { STATE_LABELS, STATE_TONES } from './syncStates';
import SPASectionHeading from '../shared/SPASectionHeading';

// Who Canvas has the assignment itself assigned to ("Assign To"); nothing when it cannot say.
const canvasAssigned = (assigned?: CourseRow['lms_assigned']) => {
  if (assigned == null) return '';

  return assigned.everyone
    ? ' Canvas assigns it to the whole course.'
    : ` Canvas assigns it to only ${assigned.assigned} of ${assigned.of}; scores for the rest are refused.`;
};

// AIDEV-NOTE: Built entirely from the assignment payload already on the page — the student rows carry
// per-course reachability and the roster carries the LMS-only list, so drilling into a course costs
// no extra NRPS call. Anything needing a fresh roster belongs behind an explicit refresh instead.
const CourseRoster = () => {
  const { courseId = '', id: assignmentId = '' } = useParams();
  // Served from the assignment page's cache — drilling into a course costs no extra LMS call.
  const { data } = useAssignmentSyncState(assignmentId);
  const students = data?.students ?? [];
  const roster = data?.roster;

  const inCourse = students.filter(s => (s.lms?.courses ?? []).includes(courseId));
  const course = (roster?.courses ?? []).find(c => c.course_id === courseId);
  const lmsOnly = roster?.lms_only?.sample ?? [];
  // Assigned here, but the LMS places them in no linked course at all.
  const stranded = students.filter(s => s.lms?.status === 'no_course' || s.lms?.status === 'never_launched');

  const sectionSearch = useSectionSearch();

  // AIDEV-NOTE: One `..`, not `../..`, even though this route's path is two segments. RoutedSPADialog
  // navigates route-relative, so `..` climbs one Route in the tree — this dialog's own — landing back
  // on the assignment. `../..` climbed past it to the grade-sync root on every close.
  return (
    <RoutedSPADialog returnPath={`..${sectionSearch}`}>
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>{`LMS course ${courseId}`}</SPADialog.Title>
          <SPADialog.Description>
            {course?.blueprint === true
              ? 'This course has no students. It accepts every score post and discards it, so sync '
                + 'looks healthy while nothing is delivered.'
              : `${course?.learners ?? 0} students in the LMS, ${inCourse.length} of them assigned this in TCI.${
                canvasAssigned(course?.lms_assigned)}`}
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          <SPASectionHeading title="Assigned in TCI and present in this course" />
          {inCourse.length === 0 && (
            <p className="tw-m-0 tw-mb-4 tw-text-sm tw-text-spa-secondary">
              Nobody assigned this assignment is in this course.
            </p>
          )}
          {inCourse.map(student => (
            <div key={student.student_id} className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <SPAPill tone={STATE_TONES[student.state]}>{STATE_LABELS[student.state]}</SPAPill>
              <span className="tw-text-spa-primary">{student.student_name ?? student.student_id}</span>
              <span className="tw-text-spa-tertiary">
                {`${student.current_score ?? '—'} → ${student.synced_score ?? '—'}`}
              </span>
            </div>
          ))}

          {stranded.length > 0 && (
            <>
              <SPASectionHeading className="tw-mt-4" title="Assigned in TCI but in no linked course" />
              {stranded.slice(0, 15).map(student => (
                <div key={student.student_id} className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-sm">
                  <SPAPill tone={student.lms?.status === 'never_launched' ? 'warning' : 'error'}>
                    {student.lms?.status === 'never_launched' ? 'Never launched' : 'In no linked course'}
                  </SPAPill>
                  <span className="tw-text-spa-primary">{student.student_name ?? student.student_id}</span>
                </div>
              ))}
              {stranded.length > 15 && (
                <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">{`and ${stranded.length - 15} more`}</p>
              )}
            </>
          )}

          {lmsOnly.length > 0 && (
            <>
              <SPASectionHeading className="tw-mt-4" title="In the LMS course but not assigned this in TCI" />
              {lmsOnly.map(person => (
                <div key={person.lms_id} className="tw-mb-1 tw-text-sm tw-text-spa-primary">
                  {person.name ?? `LMS user ${person.lms_id}`}
                  {person.name == null && (
                    <span className="tw-text-spa-tertiary"> — no TCI account linked</span>
                  )}
                </div>
              ))}
            </>
          )}
        </SPADialog.Body>

        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton type="button" variant="secondary">Close</SPAButton>
          </SPADialog.Close>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default CourseRoster;
